import type { FC } from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { navigateToSanitizedUrl } from '@wix/da-url';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import FormDropdown from '@wix/da-ds/pkg/Dropdown/Preset/FormDropdown';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import { makeXHRCall } from '@wix/da-shared-react/pkg/simpleXHR/actionCreators';
import { getIsLoading } from '@wix/da-shared-react/pkg/simpleXHR/selectors';
import {
  SimpleXHREndpoint,
  SimpleXHRMethod,
} from '@wix/da-shared-react/pkg/simpleXHR/constants';
import { updateStripeState } from '../../../../../actions/studio/earnings';
import { getCountryOptions } from '../../../constants';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  PureBiData,
  StudioEarningsWithdrawalClickBiEvent,
  StudioWithdrawalPaymentMethod,
} from '@wix/da-bi/pkg/events';

import s from './StripeWithdrawSetupModal.scss';

export interface Props {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Function to close the modal */
  requestClose: () => void;
  /** Function called when setup is successful */
  onSuccess?: () => void;
  /** Modal class name */
  className?: string;
}

const StripeWithdrawSetupModal: FC<Props> = ({
  isOpen,
  requestClose,
  onSuccess,
  className,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const logEvent = useBiLogger();
  const countryOptions = getCountryOptions(t);
  const isLoading = useSelector((state: any) =>
    getIsLoading(state, SimpleXHREndpoint.STRIPE_CONNECT)
  );
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Clear error when modal opens
  useEffect(() => {
    if (isOpen) {
      setErrorMessage('');
    }
  }, [isOpen]);

  // Clear error when user changes country
  const clearError = () => {
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleConnectToStripe = () => {
    logEvent(
      PureBiData<StudioEarningsWithdrawalClickBiEvent>({
        evid: 159,
        payment_method: StudioWithdrawalPaymentMethod.Stripe,
        process_step: 'setup',
      })
    );
    // Map country values to match PAPI types
    const countryValue = selectedCountry === 'US' ? 'usa' : 'outside_usa';

    dispatch(
      makeXHRCall({
        papiBase: 'studio',
        endpoint: SimpleXHREndpoint.STRIPE_CONNECT,
        method: SimpleXHRMethod.POST,
        data: {
          country: countryValue,
        },
        onSuccess: (response: any) => {
          const onboardingUrl = response?.onboardingUrl;
          const fee = response?.fee;

          // Update fee in redux if available
          if (fee) {
            dispatch(updateStripeState({ fee }));
          }

          if (onboardingUrl) {
            navigateToSanitizedUrl(onboardingUrl);
          } else {
            setErrorMessage(t('studio.earnings.stripe.withdraw.error.message'));
          }
        },
        onError: (response: any) => {
          setErrorMessage(
            response?.errorDescription ||
              t('studio.earnings.stripe.withdraw.error.message')
          );
        },
      })
    );
  };

  const isFormValid = selectedCountry !== '';

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={requestClose}
      className={className}
      contentClassName={s['content']}
      heading={t('studio.earnings.stripe.setup.modal.title')}
      showHeaderBorder
      showFooterBorder
      shouldCloseOnOverlayClick
      withScrollableContentWrapper
      footerRight={[
        <TextButton
          key="cancel"
          size="large"
          variant="main"
          onClick={requestClose}
        >
          {t('common.cancel')}
        </TextButton>,
        <BrandedButton
          key="connect"
          size="large"
          variant="brand-green"
          onClick={handleConnectToStripe}
          disabled={!isFormValid || isLoading}
          loading={isLoading}
        >
          {t('studio.earnings.stripe.setup.modal.buttons.connectToStripe')}
        </BrandedButton>,
      ]}
    >
      {errorMessage && (
        <div className={s['error-block']}>
          <div className={s['error-text']}>{errorMessage}</div>
        </div>
      )}

      <div className={s['inner-content']}>
        <div className={s['description']}>
          {t('studio.earnings.stripe.setup.modal.description')}
        </div>

        <div className={s['country-section']}>
          <div className={s['country-field']}>
            <label className={s['country-label']}>
              {t('studio.earnings.stripe.setup.modal.country.label')}
            </label>
            <FormDropdown
              items={countryOptions}
              selectedValue={selectedCountry}
              onSelect={value => {
                setSelectedCountry(value);
                clearError();
              }}
              placeholder={t(
                'studio.earnings.stripe.setup.modal.country.placeholder'
              )}
              className={s['country-dropdown']}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

StripeWithdrawSetupModal.displayName = 'StripeWithdrawSetupModal';

export default StripeWithdrawSetupModal;
